import React, { useEffect, useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { PageHero } from '../components/PageHero';
import { MapPin, Calendar as CalendarIcon, Filter, ChevronDown, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
// Types
interface Event {
  id: number;
  title: string;
  startDate: string;
  endDate?: string;
  location: string;
  country: string;
  disciplines: string[];
  category: 'International' | 'National' | 'Regional';
  ageGroup: 'Adult' | 'Youth' | 'All';
  month: number; // 0-11
  year: number;
}
// Mock Data
const events: Event[] = [{
  id: 1,
  title: 'Чемпіонат України з боулдерингу',
  startDate: '14 лютого 2026',
  endDate: '15 лютого 2026',
  location: 'Київ, Space',
  country: 'Україна',
  disciplines: ['Болдерінг'],
  category: 'National',
  ageGroup: 'All',
  month: 1,
  year: 2026
}, {
  id: 2,
  title: 'Кубок України (швидкість)',
  startDate: '28 лютого 2026',
  location: 'Одеса',
  country: 'Україна',
  disciplines: ['Швидкість'],
  category: 'National',
  ageGroup: 'Adult',
  month: 1,
  year: 2026
}, {
  id: 3,
  title: 'Чемпіонат області зі скелелазіння',
  startDate: '10 березня 2026',
  location: 'Львів',
  country: 'Україна',
  disciplines: ['Скелелазіння', 'Трудність'],
  category: 'Regional',
  ageGroup: 'Youth',
  month: 2,
  year: 2026
}, {
  id: 4,
  title: 'IFSC World Cup (Boulder, Speed)',
  startDate: '08 квітня 2026',
  endDate: '10 квітня 2026',
  location: 'Keqiao',
  country: 'China',
  disciplines: ['Болдерінг', 'Швидкість'],
  category: 'International',
  ageGroup: 'Adult',
  month: 3,
  year: 2026
}, {
  id: 5,
  title: 'Чемпіонат України серед молоді',
  startDate: '25 квітня 2026',
  endDate: '27 квітня 2026',
  location: 'Дніпро',
  country: 'Україна',
  disciplines: ['Болдерінг', 'Трудність', 'Швидкість'],
  category: 'National',
  ageGroup: 'Youth',
  month: 3,
  year: 2026
}];
const months = ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];
const disciplines = ['Всі', 'Скелелазіння', 'Болдерінг', 'Трудність', 'Швидкість', 'Альпінізм', 'Техніка альпінізму', 'Скайранінг', 'Скі-альпінізм', 'Льодолазіння'];
const regions = ['Всі', 'Україна', 'Міжнародні'];
const ages = ['Всі', 'Дорослі', 'Молодь'];
// Helper to check if event is in the past
const isEventPast = (event: Event): boolean => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-11
  if (event.year < currentYear) return true;
  if (event.year > currentYear) return false;
  return event.month < currentMonth;
};
export function CalendarPage() {
  const [selectedYear, setSelectedYear] = useState(2026);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null); // null = show all
  const [filterDiscipline, setFilterDiscipline] = useState('Всі');
  const [filterRegion, setFilterRegion] = useState('Всі');
  const [filterAge, setFilterAge] = useState('Всі');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Filter Logic
  const filteredEvents = events.filter(event => {
    if (event.year !== selectedYear) return false;
    if (selectedMonth !== null && event.month !== selectedMonth) return false;
    if (filterDiscipline !== 'Всі' && !event.disciplines.includes(filterDiscipline)) return false;
    if (filterRegion !== 'Всі') {
      if (filterRegion === 'Україна' && event.country !== 'Україна') return false;
      if (filterRegion === 'Міжнародні' && event.country === 'Україна') return false;
    }
    if (filterAge !== 'Всі') {
      if (filterAge === 'Дорослі' && event.ageGroup === 'Youth') return false;
      if (filterAge === 'Молодь' && event.ageGroup === 'Adult') return false;
    }
    return true;
  });
  // Group by month
  const eventsByMonth = filteredEvents.reduce((acc, event) => {
    const monthIndex = event.month;
    if (!acc[monthIndex]) acc[monthIndex] = [];
    acc[monthIndex].push(event);
    return acc;
  }, {} as Record<number, Event[]>);
  const sortedMonthIndexes = Object.keys(eventsByMonth).map(Number).sort((a, b) => a - b);
  const getDisciplineColor = (d: string) => {
    switch (d) {
      case 'Болдерінг':
        return 'text-purple-600 bg-purple-50 border-purple-100';
      case 'Швидкість':
        return 'text-red-600 bg-red-50 border-red-100';
      case 'Трудність':
        return 'text-indigo-600 bg-indigo-50 border-indigo-100';
      case 'Скелелазіння':
        return 'text-blue-600 bg-blue-50 border-blue-100';
      case 'Альпінізм':
        return 'text-emerald-600 bg-emerald-50 border-emerald-100';
      case 'Техніка альпінізму':
        return 'text-teal-600 bg-teal-50 border-teal-100';
      case 'Скайранінг':
        return 'text-orange-600 bg-orange-50 border-orange-100';
      case 'Скі-альпінізм':
        return 'text-sky-600 bg-sky-50 border-sky-100';
      case 'Льодолазіння':
        return 'text-cyan-600 bg-cyan-50 border-cyan-100';
      default:
        return 'text-primary bg-primary/5 border-primary/10';
    }
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-459">
      <Header data-id="element-460" />
      <main className="pt-20 pb-16" data-id="element-461">
        <PageHero label="Сезон 2026" title="Календар подій" subtitle="Офіційний календар змагань та заходів Федерації Альпінізму і Скелелазіння України." data-id="element-462" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12" data-id="element-463">
          <div className="flex flex-col lg:flex-row gap-12" data-id="element-464">
            {/* Sidebar: Year & Month Nav */}
            <div className="w-full lg:w-64 flex-shrink-0 space-y-8" data-id="element-465">
              {/* Year Selector */}
              <div data-id="element-466">
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-467">
                  Сезон
                </label>
                <div className="relative" data-id="element-468">
                  <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} className="w-full appearance-none bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-primary font-bold" data-id="element-469">
                    <option value={2026} data-id="element-470">2026</option>
                    <option value={2025} data-id="element-471">2025</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700" data-id="element-472">
                    <ChevronDown className="h-4 w-4" data-id="element-473" />
                  </div>
                </div>
              </div>

              {/* Month List (Desktop) */}
              <div className="hidden lg:block" data-id="element-474">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-475">
                  Місяці
                </h3>
                <ul className="space-y-1" data-id="element-476">
                  <li data-id="element-477">
                    <button onClick={() => setSelectedMonth(null)} className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${selectedMonth === null ? 'bg-primary text-white font-medium' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-478">
                      Всі місяці
                    </button>
                  </li>
                  {months.map((month, index) => <li key={month} data-id="element-479">
                      <button onClick={() => setSelectedMonth(index)} className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${selectedMonth === index ? 'bg-primary text-white font-medium' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-480">
                        {month}
                      </button>
                    </li>)}
                </ul>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0" data-id="element-481">
              {/* Filters */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 mb-8 flex flex-wrap gap-4 items-center" data-id="element-482">
                <div className="flex items-center text-gray-400 mr-2" data-id="element-483">
                  <Filter className="h-4 w-4 mr-2" data-id="element-484" />
                  <span className="text-xs font-bold uppercase tracking-wider" data-id="element-485">
                    Фільтри
                  </span>
                </div>

                <select value={filterRegion} onChange={e => setFilterRegion(e.target.value)} className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-md focus:ring-primary focus:border-primary block p-2.5" data-id="element-486">
                  {regions.map(r => <option key={r} value={r} data-id="element-487">
                      {r}
                    </option>)}
                </select>

                <select value={filterDiscipline} onChange={e => setFilterDiscipline(e.target.value)} className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-md focus:ring-primary focus:border-primary block p-2.5" data-id="element-488">
                  {disciplines.map(d => <option key={d} value={d} data-id="element-489">
                      {d}
                    </option>)}
                </select>

                <select value={filterAge} onChange={e => setFilterAge(e.target.value)} className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-md focus:ring-primary focus:border-primary block p-2.5" data-id="element-490">
                  {ages.map(a => <option key={a} value={a} data-id="element-491">
                      {a}
                    </option>)}
                </select>
              </div>

              {/* Events List */}
              <div className="space-y-12" data-id="element-492">
                {sortedMonthIndexes.length > 0 ? sortedMonthIndexes.map(monthIndex => <div key={monthIndex} data-id="element-493">
                      <h2 className="text-2xl font-heading font-bold text-primary mb-6 border-b border-gray-200 pb-2 uppercase" data-id="element-494">
                        {months[monthIndex]} {selectedYear}
                      </h2>
                      <div className="space-y-4" data-id="element-495">
                        {eventsByMonth[monthIndex].map(event => <motion.div key={event.id} initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} viewport={{
                    once: true
                  }} className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-6 flex flex-col md:flex-row gap-6 relative overflow-hidden group" data-id="element-496">
                            <div className="w-1 bg-primary absolute left-0 top-0 bottom-0 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-497" />

                            {/* Date & Tag */}
                            <div className="md:w-48 flex-shrink-0 flex flex-col justify-between" data-id="element-498">
                              <div data-id="element-499">
                                <div className="text-sm font-bold text-gray-900" data-id="element-500">
                                  {event.startDate}
                                  {event.endDate && <span className="text-gray-500 font-normal block text-xs mt-1" data-id="element-501">
                                      {' '}
                                      - {event.endDate}
                                    </span>}
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-1.5 mt-3 md:mt-0" data-id="element-502">
                                {event.disciplines.map(d => <span key={d} className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium border ${getDisciplineColor(d)}`} data-id="element-503">
                                    {d}
                                  </span>)}
                              </div>
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0" data-id="element-504">
                              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors" data-id="element-505">
                                {event.title}
                              </h3>
                              <div className="flex items-center text-gray-500 text-sm mb-4" data-id="element-506">
                                <MapPin className="h-4 w-4 mr-1.5 flex-shrink-0" data-id="element-507" />
                                {event.location}, {event.country}
                              </div>

                              <div className="flex items-center gap-4 text-xs font-medium" data-id="element-508">
                                <button className="text-primary hover:text-primary-dark hover:underline flex items-center" data-id="element-509">
                                  Інфолист
                                  <ExternalLink className="h-3 w-3 ml-1" data-id="element-510" />
                                </button>
                                {isEventPast(event) ? <button className="text-primary hover:text-primary-dark hover:underline flex items-center" data-id="element-511">
                                    Результати
                                    <ExternalLink className="h-3 w-3 ml-1" data-id="element-512" />
                                  </button> : <button className="text-primary hover:text-primary-dark hover:underline flex items-center" data-id="element-513">
                                    Реєстрація
                                    <ExternalLink className="h-3 w-3 ml-1" data-id="element-514" />
                                  </button>}
                              </div>
                            </div>
                          </motion.div>)}
                      </div>
                    </div>) : <div className="text-center py-20 bg-white rounded-lg border border-gray-200 border-dashed" data-id="element-515">
                    <CalendarIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" data-id="element-516" />
                    <p className="text-gray-500 font-medium" data-id="element-517">
                      Подій не знайдено за вибраними фільтрами
                    </p>
                    <button onClick={() => {
                  setFilterDiscipline('Всі');
                  setFilterRegion('Всі');
                  setFilterAge('Всі');
                  setSelectedMonth(null);
                }} className="mt-4 text-primary text-sm hover:underline" data-id="element-518">
                      Скинути фільтри
                    </button>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-519" />
    </div>;
}